'use client'

import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { Button, Form } from 'react-bootstrap'
import Link from 'next/link'
import LogoIcon from '@/components/LogoIcon'
import IconifyIcon from '@/components/wrappers/IconifyIcon'
import { useBiometricLogin } from '@/hooks/useBiometricLogin'
import { useSignIn } from '../useSignIn'
import { useNotificationContext } from '@/context/useNotificationContext'
import styles from '../login.module.css'

const LoginForm = () => {
  const searchParams = useSearchParams()
  const { t } = useTranslation()
  const { showNotification } = useNotificationContext()
  const { form, login, loginWithCredentials, loading } = useSignIn()
  const {
    register,
    setValue,
    formState: { errors },
    watch,
  } = form

  const pinValue = watch('pin') || '    '

  const {
    biometricAvailable,
    biometricReady,
    biometricLabel,
    biometricLoading,
    loginWithBiometric,
    refreshBiometric,
  } = useBiometricLogin({ onCredentials: loginWithCredentials })

  useEffect(() => {
    void refreshBiometric()
  }, [refreshBiometric])

  const handleInputChange = (index: number, value: string) => {
    if (value && !/^\d+$/.test(value)) return

    const pinArray = pinValue.split('')
    pinArray[index] = value
    const newPin = pinArray.join('').padEnd(4, ' ')
    setValue('pin', newPin)

    if (value) {
      const nextInput = document.getElementById(`pin-${index + 1}`)
      if (nextInput) nextInput.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      const pinArray = pinValue.split('')
      if (pinArray[index] && pinArray[index] !== ' ') {
        pinArray[index] = ' '
        setValue('pin', pinArray.join(''))
      } else if (index > 0) {
        const prevInput = document.getElementById(`pin-${index - 1}`)
        if (prevInput) {
          prevInput.focus()
          pinArray[index - 1] = ' '
          setValue('pin', pinArray.join(''))
        }
      }
    }
  }

  useEffect(() => {
    if (!pinValue) setValue('pin', '    ')
  }, [pinValue, setValue])

  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      showNotification({
        message: t('auth.checkoutSuccess'),
        variant: 'success',
      })
    }
  }, [searchParams, showNotification, t])

  useEffect(() => {
    if (pinValue && pinValue.length === 4 && !pinValue.includes(' ')) {
      login()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinValue])

  const showBiometric = biometricAvailable && biometricReady

  return (
    <div className={styles.loginForm}>
      <div className={styles.loginFormCard}>
        <div className={`${styles.logoWrap} d-none d-lg-block`}>
          <LogoIcon size={48} />
        </div>
        <div className={`${styles.logoWrap} d-lg-none`}>
          <LogoIcon size={56} />
        </div>

        <div className={styles.loginIntro}>
          <p className={styles.loginEyebrow}>{t('auth.loginEyebrow', { defaultValue: 'One login for your chain' })}</p>
          <h2 className={styles.loginTitle}>{t('auth.loginTitle')}</h2>
          <p className={styles.loginSubtitle}>
            {t('auth.loginSubtitleUnified', {
              defaultValue:
                'Chain admins, managers, and budtenders use the same login. We open the right dashboard for your role.',
            })}
          </p>
        </div>

        {showBiometric && (
          <div className={styles.biometricBlock}>
            <button
              type="button"
              className={styles.biometricButton}
              onClick={() => void loginWithBiometric()}
              disabled={loading || biometricLoading}
            >
              <IconifyIcon icon="solar:face-scan-circle-bold-duotone" width={22} height={22} />
              <span>{biometricLoading ? t('auth.verifyingBiometric', { defaultValue: 'Verifying…' }) : biometricLabel}</span>
            </button>
            <p className={styles.biometricHint}>
              {t('auth.orSignInWithPin', { defaultValue: 'Or sign in with your username and PIN' })}
            </p>
          </div>
        )}

        <Form onSubmit={login} className={styles.loginFields}>
          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.fieldLabel}>{t('auth.usernameOrEmail')}</Form.Label>
            <Form.Control
              type="text"
              {...register('identifier')}
              placeholder={t('auth.usernamePlaceholder')}
              isInvalid={!!errors.identifier}
              className={styles.textInput}
            />
            <Form.Control.Feedback type="invalid">
              {errors.identifier?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label className={styles.fieldLabel}>{t('auth.pinLabel')}</Form.Label>
            <div className={styles.pinRow}>
              {[0, 1, 2, 3].map((i) => (
                <Form.Control
                  key={i}
                  id={`pin-${i}`}
                  type="password"
                  maxLength={1}
                  inputMode="numeric"
                  className={styles.pinInput}
                  value={pinValue[i] === ' ' ? '' : pinValue[i] || ''}
                  onChange={(e) => handleInputChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                />
              ))}
            </div>
            <input type="hidden" {...register('pin')} />
            {errors.pin && <div className="text-danger small mt-2">{errors.pin.message}</div>}
            <div className={styles.forgotPinRow}>
              <Link href="/auth/forgot-password" className={styles.forgotPinLink}>
                {t('auth.forgotPin', { defaultValue: 'Forgot PIN?' })}
              </Link>
            </div>
          </Form.Group>

          <Button type="submit" disabled={loading} className={styles.submitButton}>
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </Button>

          <div className={styles.loginFooter}>
            <p className={styles.signUpRow}>
              {t('auth.noAccount')}{' '}
              <Link href="/auth/signup" className={styles.signUpLink}>
                {t('auth.signUp')}
              </Link>
            </p>
            <Link href="/privacy" className={styles.privacyLink}>
              {t('auth.privacyPolicy', { defaultValue: 'Privacy Policy' })}
            </Link>
          </div>
        </Form>
      </div>
    </div>
  )
}

export default LoginForm
